import * as Sentry from "@sentry/react";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "wouter";
import { useAdminBasePath } from "@/lib/adminPath";
import { 
  ShoppingCart, 
  Package, 
  Clock, 
  Users, 
  Eye, 
  Edit, 
  Trash2,
  AlertTriangle,
  CheckCircle,
  FileSearch,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Truck,
  Receipt,
  MoreHorizontal,
  Download,
  FileText,
  Loader2,
  Shield,
  X as XIcon,
  Plus,
  Merge,
  ExternalLink,
  Tablet,
  HelpCircle,
  ListFilter
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ContextMenu, ContextMenuContent, ContextMenuTrigger } from "@/components/ui/context-menu";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { apiRequest } from "@/lib/queryClient";
import { summarizeQuickbooksSend, type QuickbooksSendReport } from "@/lib/quickbooksSendReport";
import { refreshAfterQuickbooksSend } from "@/lib/quickbooksSendRefresh";
import { apiSetOrderStatus, apiDeleteOrder, openPickSheetInNewWindow } from "@/hooks/useOrderActions";
import { OrderActionItems } from "@/components/order-action-items";
import { QbSyncedMark } from "@/components/qb-synced-mark";
import { OrderSourceMark } from "@/components/agent-order-bubble";
import { OrderSlip } from "@/components/order-slip";
import { QBCustomerLinkDialog, UnlinkedAccountWithMatches, LinkDecision } from "@/components/qb-customer-link-dialog";
import { QBProductLinkDialog, UnlinkedProductWithMatches, ProductLinkDecision } from "@/components/qb-product-link-dialog";
import { AdminOrderModal } from "@/components/admin-order-modal";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import * as React from "react";

import type { OrderWithDetails, DashboardStats, Account, OrderWindowConfig } from "@shared/schema";
import {
  EMPTY_FILTER_STATE,
  filterStateToFulfillmentDateParam,
  filterStateToSourceParam,
  matchesFilters,
  type OrderFilterState,
  type OrderForFilter,
  type OrderStatus,
} from "@/lib/order-filters";
import { OrdersFilterToolbar } from "@/components/orders-filter-toolbar";
import { useOrganization } from "@/contexts/OrganizationContext";

// Support Mode Banner Component
function SupportModeBanner({ orgName, onExit }: { orgName: string; onExit: () => void }) {
  return (
    <div className="bg-amber-500 text-amber-950 py-2 px-4 flex items-center justify-between shadow-md" data-testid="support-mode-banner">
      <div className="flex items-center gap-3">
        <Shield className="h-5 w-5" />
        <span className="font-medium">
          Support Mode: Viewing <strong>{orgName}</strong> dashboard as Super Admin
        </span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onExit}
        className="hover:bg-amber-600 text-amber-950"
        data-testid="btn-exit-support-mode"
      >
        <XIcon className="h-4 w-4 mr-1" />
        Exit to Super Admin
      </Button>
    </div>
  );
}

const ORDERS_PAGE_SIZE_KEY = 'freshorder:ordersPageSize';
const VALID_PAGE_SIZES = [10, 25, 50, 100];

// Phase 4c summary strip — one breakdown segment ("5 pending"). Numbers are
// tabular so the strip doesn't jitter as counts change under a chip edit;
// a zero recedes (regular weight, muted, still AA) so the eye lands on the
// non-zero figures. Display-only; the strip is a readout, never a control.
// Note: the theme tokens are plain `var(--x)` strings, so Tailwind opacity
// modifiers (`text-muted-foreground/60`) silently generate nothing here.
const countFormatter = new Intl.NumberFormat();
function SummaryCount({ value, label, testId }: { value: number; label: string; testId: string }) {
  const zero = value === 0;
  return (
    <span className="inline-flex items-baseline gap-1 whitespace-nowrap text-muted-foreground" data-testid={testId}>
      <span className={`tabular-nums ${zero ? "font-normal" : "font-medium text-foreground"}`}>
        {countFormatter.format(value)}
      </span>
      {label}
    </span>
  );
}

export default function SellerDashboard() {
  // All hooks must be declared at the top, before any conditional returns
  
  const { organization } = useOrganization();

  // Check for impersonation/support mode
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedOrgName, setImpersonatedOrgName] = useState("");
  const [impersonationVerified, setImpersonationVerified] = useState(false);
  
  // UI state hooks - must be at top level

  // Unified filter-state object (Phase 4a + 4b). Every filter surface — the
  // chip toolbar, the Add-filter menu, the table query, the eventual
  // summary strip — reads and writes this one object. The composition
  // predicate (within-axis OR, across-axis AND) and the Order Window
  // semantics live in @/lib/order-filters.
  const [filterState, setFilterState] = useState<OrderFilterState>(EMPTY_FILTER_STATE);
  const hasSetInitialDateFilter = React.useRef(false);

  // Phase 4d — rows that should fade out before vanishing. Populated by
  // handleStatusUpdate when a status change un-matches an active chip; the
  // entry is removed after the ~300ms fade, at which point the query refetch
  // actually removes the row from the list.
  const [fadingOrderIds, setFadingOrderIds] = useState<Set<number>>(new Set());

  // F2 — fade timers, keyed by orderId. A second status change on the same
  // row inside the 300ms window replaces (clears) the first timer rather than
  // stacking a second one, and every outstanding timer is cleared on unmount
  // so a late refresh() can't fire against a torn-down page.
  const fadeTimersRef = React.useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());
  useEffect(() => {
    const timers = fadeTimersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  // F2 — the status this client last set per row, until the server's copy
  // catches up. `orders` lags the PATCH by one refetch (300ms + network on
  // the fade path), so two changes on one row inside that window would
  // otherwise both read the same pre-change status and the second Undo
  // would rewind past the first change. handleStatusUpdate reads prevStatus
  // from here first and falls back to the fetched row. `prev` is the status
  // the write replaced: a fetched row showing neither `status` nor `prev`
  // means someone else (batch action, context menu, another tab) wrote the
  // row, and the entry must yield to the server.
  type LastSet = { status: OrderStatus; prev: OrderStatus; at: number };
  const lastSetStatusRef = React.useRef<Map<number, LastSet>>(new Map());
  const [selectedOrder, setSelectedOrder] = useState<OrderWithDetails | null>(null);
  const [isOrderSlipOpen, setIsOrderSlipOpen] = useState(false);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState<number | null>(null);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{key: string, direction: 'asc' | 'desc'} | null>({ key: 'createdAt', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    if (typeof window === 'undefined') return 25;
    const stored = parseInt(localStorage.getItem(ORDERS_PAGE_SIZE_KEY) || '', 10);
    return VALID_PAGE_SIZES.includes(stored) ? stored : 25;
  });
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Explicit list of orders the confirmation dialog is currently asking about.
  // Set by requestDelete(orderIds); read by confirmDelete. Replaces the prior
  // pattern of overwriting selectedOrderIds to "pass" the per-row delete target
  // into the dialog. See Phase 3a brief — useOrderActions must not touch
  // selection state.
  const [pendingDeleteOrderIds, setPendingDeleteOrderIds] = useState<number[]>([]);
  const [isMergeDialogOpen, setIsMergeDialogOpen] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  
  // QB Customer Link Dialog state
  const [showQBLinkDialog, setShowQBLinkDialog] = useState(false);
  const [unlinkedAccountsWithMatches, setUnlinkedAccountsWithMatches] = useState<UnlinkedAccountWithMatches[]>([]);
  const [pendingLinkDecisions, setPendingLinkDecisions] = useState<LinkDecision[]>([]);
  const [isCheckingQBMatches, setIsCheckingQBMatches] = useState(false);
  const [singleOrderQBSending, setSingleOrderQBSending] = useState(false);
  
  // QB Product Link Dialog state
  const [showQBProductLinkDialog, setShowQBProductLinkDialog] = useState(false);
  const [unlinkedProductsWithMatches, setUnlinkedProductsWithMatches] = useState<UnlinkedProductWithMatches[]>([]);
  const [pendingOrderDataForProductLink, setPendingOrderDataForProductLink] = useState<OrderWithDetails[]>([]);
  const [pendingCustomerLinkDecisions, setPendingCustomerLinkDecisions] = useState<LinkDecision[] | undefined>(undefined);
  const [pendingConfirmedResend, setPendingConfirmedResend] = useState(false);
  
  // Broken link detection state (for customers deleted from QuickBooks)
  const [showBrokenLinkDialog, setShowBrokenLinkDialog] = useState(false);
  const [brokenLinkConfirming, setBrokenLinkConfirming] = useState(false);
  const [brokenLinkAccounts, setBrokenLinkAccounts] = useState<Array<{
    accountId: number;
    accountName: string;
    storedQbCustomerId: string;
    potentialMatches: Array<{
      id: string;
      displayName: string;
      companyName?: string;
      email?: string;
      matchType: 'exact' | 'partial';
    }>;
  }>>([]);
  
  // Batch processing progress state
  const [processingProgress, setProcessingProgress] = useState<{
    show: boolean;
    orderId?: number;
    customerName?: string;
    totalItems?: number;
    processedItems?: number;
    currentItem?: string;
    status?: string;
    error?: string;
  }>({ show: false });

  // What the last QuickBooks send left for the admin to act on: orders that were
  // not sent, and sent invoices with lines at the placeholder price. Stays on
  // screen until dismissed — the toast only counts.
  const [sendReport, setSendReport] = useState<QuickbooksSendReport | null>(null);
  
  // Admin order creation modal state
  const [isAdminOrderModalOpen, setIsAdminOrderModalOpen] = useState(false);
  
  // Resend to QB confirmation state (for orders already sent)
  const [showResendConfirmDialog, setShowResendConfirmDialog] = useState(false);
  const [alreadySentOrders, setAlreadySentOrders] = useState<OrderWithDetails[]>([]);
  // When set, the resend dialog's "Send Anyway" re-dispatches the single-order path
  // instead of the batch path. Clears after confirm/cancel.
  const [pendingSingleResendOrderId, setPendingSingleResendOrderId] = useState<number | null>(null);
  
  const [isLaunchingKiosk, setIsLaunchingKiosk] = useState(false);
  
  // State for kiosk launch dialog (used instead of popup to avoid Chrome blocking)
  const [showKioskLaunchDialog, setShowKioskLaunchDialog] = useState(false);
  const [kioskLaunchUrl, setKioskLaunchUrl] = useState("");
  
  // Other hooks
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const adminBase = useAdminBasePath();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  // Account form schema
  const accountSchema = z.object({
    name: z.string().min(1, "Name is required"),
    phone: z.string().min(1, "Phone is required"),
    customUrl: z.string(),
    isActive: z.boolean()
  });

  type AccountForm = z.infer<typeof accountSchema>;

  const form = useForm<AccountForm>({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      name: "",
      phone: "",
      customUrl: "",
      isActive: true
    }
  });

  // Auto-refresh when switching to dashboard
  useEffect(() => {
    if (location === "/dashboard") {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
    }
  }, [location, queryClient]);

  // Fetch dashboard stats with auto-refresh
  const { data: stats, isLoading: statsLoading } = useQuery<DashboardStats>({
    queryKey: ["/api/dashboard/stats"],
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Lightweight orders query for building date filter tabs — no items needed
  const { data: dateOrders = [] } = useQuery<any[]>({
    queryKey: ["/api/orders", { includeItems: false, limit: 200 }],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/orders?includeItems=false&limit=200");
      return res.json();
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // Orders query — narrows server-side by Order Window when one is active.
  // The Order Window value (specific YYYY-MM-DD or "unscheduled") maps 1:1 to
  // the /api/orders `fulfillmentDate` param via filterStateToFulfillmentDateParam.
  // Status / fulfillment-type / QB chips filter the response client-side via
  // matchesFilters.
  const dateParam = filterStateToFulfillmentDateParam(filterState);
  // Order Source narrows on the wire too (`source=sms_agent`), so one "text
  // orders" chip is one click even past the 200-row cap.
  const sourceParam = filterStateToSourceParam(filterState);
  const { data: orders = [], isLoading: ordersLoading } = useQuery<OrderWithDetails[]>({
    queryKey: ["/api/orders", { fulfillmentDate: dateParam ?? undefined, source: sourceParam ?? undefined }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "200" });
      if (dateParam) params.set("fulfillmentDate", dateParam);
      if (sourceParam) params.set("source", sourceParam);
      const res = await apiRequest("GET", `/api/orders?${params}`);
      return res.json();
    },
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });

  // F2 — retire lastSetStatusRef entries when the fetched row agrees with
  // what this client last set (the server has caught up), when it shows a
  // status this client never wrote (a foreign write superseded ours), or
  // after 10s regardless as a backstop.
  useEffect(() => {
    const ref = lastSetStatusRef.current;
    if (ref.size === 0) return;
    const now = Date.now();
    ref.forEach((entry, id) => {
      const fetched = orders.find(o => o.id === id)?.status;
      const caughtUp = fetched === entry.status;
      const foreignWrite = fetched !== undefined && fetched !== entry.prev && !caughtUp;
      if (caughtUp || foreignWrite || now - entry.at > 10_000) ref.delete(id);
    });
  }, [orders]);

  // Deep link: ?order=XXX — auto-open the order detail modal.
  // Read the param once at render time (before any re-renders strip it).
  const deepLinkOrderId = React.useRef<number | null>(
    (() => {
      const p = new URLSearchParams(window.location.search).get("order");
      if (!p) return null;
      const n = parseInt(p, 10);
      return isNaN(n) ? null : n;
    })()
  );
  const deepLinkHandled = React.useRef(false);

  useEffect(() => {
    console.log('Deep link effect:', { orderId: deepLinkOrderId.current, ordersLength: orders.length, ordersLoading, handled: deepLinkHandled.current });
    const targetId = deepLinkOrderId.current;
    if (!targetId || deepLinkHandled.current || ordersLoading) return;

    const match = orders.find(o => o.id === targetId);
    if (match) {
      // Found — open the modal and clean the URL
      setSelectedOrder(match);
      setIsOrderSlipOpen(true);
      window.history.replaceState(null, "", window.location.pathname);
      deepLinkHandled.current = true;
    } else if (!ordersLoading && filterState.date != null) {
      // Not found in current Order Window — clear the window chip so the next
      // fetch returns the full (200-row) set and the order can be located.
      setFilterState(s => ({ ...s, date: null }));
    } else if (!ordersLoading && filterState.date == null) {
      // Already unfiltered by window and still not found — give up.
      window.history.replaceState(null, "", window.location.pathname);
      deepLinkHandled.current = true;
    }
  }, [orders, ordersLoading, filterState.date]);

  // Fetch order window configuration for the organization
  const { data: windowConfig } = useQuery<OrderWindowConfig>({
    queryKey: ["/api/order-window-config"],
  });

  const showPickupScheduling = (windowConfig as any)?.pickupSchedulingEnabled ?? false;
  const deliveryEnabled = (windowConfig as any)?.deliveryEnabled ?? true;
  const pickupEnabled = (windowConfig as any)?.pickupEnabled ?? false;
  const showFulfillmentColumn = deliveryEnabled || pickupEnabled || showPickupScheduling;

  React.useEffect(() => {
    if (!showFulfillmentColumn) {
      // No fulfillment configured: Order Window + fulfillment-type axes are
      // both meaningless. Clear them and demote any fulfillment-keyed sort.
      setFilterState(s => ({ ...s, date: null, fulfillmentTypes: [] }));
      if (sortConfig?.key === 'pickupDate' || sortConfig?.key === 'fulfillmentDate') {
        setSortConfig({ key: 'createdAt', direction: 'desc' });
      }
    }
  }, [showFulfillmentColumn]);


  // Impersonation status check — server-derived from the two cookies
  // (super_admin_session + admin_session). One-shot on mount; cookie expiry
  // is server-enforced so no client-side revalidation interval is needed.
  // For a regular admin (no super_admin_session), the endpoint returns
  // { isImpersonating: false } and this dashboard renders normally without
  // the support-mode banner / exit-impersonation button.
  useEffect(() => {
    let cancelled = false;
    const checkImpersonation = async () => {
      try {
        const response = await fetch('/api/super-admin/impersonation-status', {
          credentials: 'include',
        });
        const result = await response.json().catch(() => ({ isImpersonating: false }));
        if (cancelled) return;

        if (result.isImpersonating && result.organization) {
          setIsImpersonating(true);
          setImpersonatedOrgName(result.organization.name);
        }
      } catch {
        // Network error — treat as non-impersonation (regular admin path)
      } finally {
        if (!cancelled) setImpersonationVerified(true);
      }
    };

    checkImpersonation();
    return () => { cancelled = true; };
  }, []);

  // NOTE: Impersonation loading state is rendered conditionally in the main JSX
  // to avoid breaking React's rules of hooks. We only block render briefly while
  // the status check is in flight.
  const isImpersonationLoading = !impersonationVerified;

  const handleExitSupportMode = async () => {
    try {
      await fetch('/api/super-admin/exit-impersonation', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Best-effort — server clears the cookie on success; redirect either way
    }

    // Clear UI metadata sessionStorage that the admin flow leaves behind.
    // (Auth tokens are no longer in sessionStorage; cookies are cleared by the server.)
    sessionStorage.removeItem('adminUser');
    sessionStorage.removeItem('orgBranding');

    window.location.href = '/super-admin';
  };

  const handleAccountEdit = (account: Account) => {
    setEditingAccount(account);
    form.reset({
      name: account.name,
      phone: account.phone || "",
      customUrl: account.customUrl || "",
      isActive: account.isActive
    });
    setIsEditDialogOpen(true);
  };

  // Auto-generate custom URL from name
  const generateCustomUrl = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Watch name field to auto-generate URL
  const watchedName = form.watch("name");
  React.useEffect(() => {
    if (watchedName && editingAccount) {
      const generatedUrl = generateCustomUrl(watchedName);
      form.setValue("customUrl", generatedUrl);
    }
  }, [watchedName, form, editingAccount]);

  const handleSubmit = async (data: AccountForm) => {
    try {
      if (editingAccount) {
        await apiRequest("PATCH", `/api/accounts/${editingAccount.id}`, data);
        toast({
          title: "Account updated",
          description: "Account has been updated successfully.",
        });
      }
      queryClient.invalidateQueries({ queryKey: ['/api/accounts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
      setIsEditDialogOpen(false);
      setEditingAccount(null);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Failed to save account",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleStatusUpdate = async (orderId: number, newStatus: "pending" | "reviewed" | "cancelled") => {
    // Phase 4d: fade-out + undo toast on status change.
    //
    // If the new status would un-match an active chip, the row's about to
    // vanish from the visible list — fade it for ~300ms first so the eye
    // tracks where it went, then invalidate (which actually removes it).
    // Always show an undo toast: confirmation the change saved + a short
    // safety net.
    const order = orders.find(o => o.id === orderId);
    // F2: prefer the status this client last set for the row (the fetched
    // row lags the PATCH by a refetch), so back-to-back changes each undo to
    // the status they actually replaced. Captured as a const here: the Undo
    // closure below owns this value for the life of the toast and never
    // re-reads the mutable `order`.
    const prevStatus: OrderStatus | undefined =
      lastSetStatusRef.current.get(orderId)?.status ??
      (order?.status as OrderStatus | undefined);
    if (!order || !prevStatus) return;

    // `order` is only consulted for its non-status fields here; status comes
    // from prevStatus/newStatus, which are the fresh values.
    const wouldUnmatch = !matchesFilters(
      { ...(order as OrderForFilter), status: newStatus },
      filterState
    );

    // Record the intended write before the PATCH is in flight, so a second
    // click on the same row while this one is still pending already sees it.
    // Rolled back below if the PATCH fails.
    const priorEntry = lastSetStatusRef.current.get(orderId);
    lastSetStatusRef.current.set(orderId, { status: newStatus, prev: prevStatus, at: Date.now() });

    try {
      await apiSetOrderStatus(orderId, newStatus);

      const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
        queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
      };

      // Any fade already scheduled for this row is superseded by this change.
      const pendingFade = fadeTimersRef.current.get(orderId);
      if (pendingFade) {
        clearTimeout(pendingFade);
        fadeTimersRef.current.delete(orderId);
      }

      if (wouldUnmatch) {
        setFadingOrderIds(prev => {
          const next = new Set(prev);
          next.add(orderId);
          return next;
        });
        const timer = setTimeout(() => {
          fadeTimersRef.current.delete(orderId);
          refresh();
          setFadingOrderIds(prev => {
            const next = new Set(prev);
            next.delete(orderId);
            return next;
          });
        }, 300);
        fadeTimersRef.current.set(orderId, timer);
      } else {
        // The row stays visible under the active chips — if a superseded
        // change had started fading it, bring it back now.
        setFadingOrderIds(prev => {
          if (!prev.has(orderId)) return prev;
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        refresh();
      }

      setStatusPopoverOpen(null);

      const orderLabel = `ORD-${orderId.toString().padStart(3, '0')}`;
      toast({
        title: `${orderLabel} → ${newStatus}`,
        action: (
          <ToastAction
            altText={`Undo status change to ${newStatus}`}
            onClick={async () => {
              const beforeUndo = lastSetStatusRef.current.get(orderId);
              lastSetStatusRef.current.set(orderId, { status: prevStatus, prev: newStatus, at: Date.now() });
              try {
                await apiSetOrderStatus(orderId, prevStatus);
                queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
                queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
              } catch (error: any) {
                if (beforeUndo) lastSetStatusRef.current.set(orderId, beforeUndo);
                else lastSetStatusRef.current.delete(orderId);
                toast({
                  title: "Undo failed",
                  description: error?.message || "Could not revert status",
                  variant: "destructive",
                });
              }
            }}
          >
            Undo
          </ToastAction>
        ),
      });
    } catch (error: any) {
      if (priorEntry) lastSetStatusRef.current.set(orderId, priorEntry);
      else lastSetStatusRef.current.delete(orderId);
      toast({
        title: "Failed to update status",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleBatchStatusUpdate = async (newStatus: "pending" | "reviewed") => {
    if (selectedOrderIds.size === 0) {
      toast({
        title: "No orders selected",
        description: "Please select orders to update.",
        variant: "destructive",
      });
      return;
    }

    const orderIds = Array.from(selectedOrderIds);
    const statusLabel = newStatus;
    let successCount = 0;
    let failedIds: number[] = [];

    // Process sequentially to avoid overwhelming the server
    for (const orderId of orderIds) {
      try {
        await apiSetOrderStatus(orderId, newStatus);
        // Keep the F2 per-row ledger in step with this write (see lastSetStatusRef).
        const prev =
          lastSetStatusRef.current.get(orderId)?.status ??
          (orders.find(o => o.id === orderId)?.status as OrderStatus | undefined) ??
          newStatus;
        lastSetStatusRef.current.set(orderId, { status: newStatus, prev, at: Date.now() });
        successCount++;
      } catch (error) {
        failedIds.push(orderId);
      }
    }

    // Always invalidate queries to refresh UI with current server state
    queryClient.invalidateQueries({ queryKey: ['/api/orders'] });
    queryClient.invalidateQueries({ queryKey: ['/api/dashboard/stats'] });
    setSelectedOrderIds(new Set());

    if (failedIds.length === 0) {
      toast({
        title: "Orders updated",
        description: `${successCount} order${successCount > 1 ? 's' : ''} changed to ${statusLabel}`,
      });
    } else if (successCount > 0) {
      toast({
        title: "Partial update",
        description: `${successCount} order${successCount > 1 ? 's' : ''} updated, ${failedIds.length} failed`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Failed to update orders",
        description: "Please try again later.",
        variant: "destructive",
      });
    }
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    try {
      localStorage.setItem(ORDERS_PAGE_SIZE_KEY, String(newRowsPerPage));
    } catch {
      // localStorage can throw in private mode / quota issues — fail silent
    }
  };




  const availableFulfillmentDates = React.useMemo(() => {
    const getFulfillmentDate = (order: any) => order.fulfillmentDate || order.pickupDate;
    const getFulfillmentType = (order: any) => order.fulfillmentType || (order.pickupDate ? "pickup" : null);

    // When a fulfillment-type chip is active, the Order Window picker should
    // only show windows that contain matching orders. With the multi-select
    // chip model, having no fulfillment-type chips means "any type."
    let relevantOrders = dateOrders;
    if (filterState.fulfillmentTypes.length > 0) {
      relevantOrders = dateOrders.filter((order: any) => {
        const type = getFulfillmentType(order);
        return type !== null && filterState.fulfillmentTypes.includes(type);
      });
    }

    const dateMap = new Map<string, number>();
    let unscheduledCount = 0;
    relevantOrders.forEach(order => {
      const date = getFulfillmentDate(order);
      if (date) {
        dateMap.set(date, (dateMap.get(date) || 0) + 1);
      } else {
        unscheduledCount++;
      }
    });

    const tz = (windowConfig as any)?.timezone || 'America/Toronto';
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Newest first: the picker opens on the latest windows, not the oldest.
    const sortedDates = Array.from(dateMap.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateStr, count]) => {
        const [year, month, day] = dateStr.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const diffDays = Math.round((date.getTime() - todayStart.getTime()) / (1000 * 60 * 60 * 24));
        let label: string;
        if (diffDays === 0) label = `Today, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        else if (diffDays === 1) label = `Tomorrow, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        else label = `${dayNames[date.getDay()]}, ${monthNames[date.getMonth()]} ${date.getDate()}`;
        return { value: dateStr, label, count };
      });

    return { dates: sortedDates, unscheduledCount };
  }, [dateOrders, filterState.fulfillmentTypes, windowConfig]);

  // Window cleanup: if the active Order Window no longer appears in the
  // available windows (e.g. the orders that defined it were deleted), drop
  // the chip rather than leaving a dead filter that hides every row.
  React.useEffect(() => {
    const active = filterState.date;
    if (!active) return;
    if (active.kind === "unscheduled") {
      if (availableFulfillmentDates.unscheduledCount === 0) {
        setFilterState(s => ({ ...s, date: null }));
      }
      return;
    }
    const dateExists = availableFulfillmentDates.dates.some(d => d.value === active.value);
    if (!dateExists) setFilterState(s => ({ ...s, date: null }));
  }, [availableFulfillmentDates, filterState.date]);

  // Page-load default: a single Order Window chip set to the current window
  // (today if there are orders for it; otherwise the next upcoming window;
  // otherwise the most recent past window).
  React.useEffect(() => {
    if (hasSetInitialDateFilter.current) return;
    if (availableFulfillmentDates.dates.length === 0) return;
    const tz = (windowConfig as any)?.timezone || 'America/Toronto';
    const nowLocal = new Date(new Date().toLocaleString('en-US', { timeZone: tz }));
    const todayStr = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, '0')}-${String(nowLocal.getDate()).padStart(2, '0')}`;
    // Dates run newest first, so the nearest upcoming window is the last one
    // still >= today; with none upcoming, the first entry is the latest past.
    const upcoming = availableFulfillmentDates.dates.filter(d => d.value >= todayStr);
    const nearestDate = upcoming[upcoming.length - 1] || availableFulfillmentDates.dates[0];
    if (nearestDate) {
      setFilterState(s => ({ ...s, date: { kind: "specific", value: nearestDate.value } }));
    }
    hasSetInitialDateFilter.current = true;
  }, [availableFulfillmentDates, windowConfig]);

  // Compose all filter axes via matchesFilters (within-axis OR, across-axis AND).
  const filteredOrders = React.useMemo(
    () => orders.filter(order => matchesFilters(order as OrderForFilter, filterState)),
    [orders, filterState]
  );

  // Phase 4c — summary strip counts. Reflects ALL active filters (every axis,
  // no exceptions) per the locked decision in phase-4-brief.md: a strip where
  // some filters move the numbers and some don't reads as inconsistent. A
  // sometimes-redundant number (e.g. "22 pending · 0 reviewed · 0 sent to QB"
  // when a Pending chip is active) is the accepted v1 cost of consistency.
  //
  // `truncated` is true when the server response is exactly at the 200-row
  // cap — the cap is on `/api/orders` itself, not on filteredOrders, so we
  // key off `orders.length` not `filteredOrders.length`. False positives
  // (exactly 200 actual matches) are acceptable for the v1 indicator.
  //
  // v2 deferred (do not build here, see brief Phase 4c §6):
  //   (a) clickable strip segments as filter shortcuts
  //   (b) revisiting whether the strip should be an unscoped overview rather
  //       than reflecting all filters
  const summary = React.useMemo(() => {
    let pending = 0, reviewed = 0, cancelled = 0, sentToQb = 0;
    for (const o of filteredOrders) {
      if (o.status === "pending") pending++;
      else if (o.status === "reviewed") reviewed++;
      else if (o.status === "cancelled") cancelled++;
      if ((o as any).quickbooksSyncedAt != null) sentToQb++;
    }
    return {
      total: filteredOrders.length,
      // `loaded` is the server response for the active Order Window (or all,
      // capped at 200). `narrowed` is true when the client-side chips cut that
      // set down — that's the moment the strip says "12 of 87".
      loaded: orders.length,
      narrowed: filteredOrders.length < orders.length,
      pending,
      reviewed,
      cancelled,
      sentToQb,
      truncated: orders.length >= 200,
    };
  }, [filteredOrders, orders.length]);

  // Zero-state copy for the strip. Distinguishes "the chips filtered
  // everything out" (recoverable by loosening a chip) from "this window has
  // nothing" from "this org has nothing yet".
  const summaryEmptyReason =
    summary.total > 0 ? null
    : summary.loaded > 0 ? "none match the active filters"
    : filterState.date ? "none in this window yet"
    : "none yet";

  const sortedOrders = React.useMemo(() => {
    if (!filteredOrders) return [];
    if (!sortConfig) return filteredOrders;

    return [...filteredOrders].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      if (sortConfig.key === 'account.name') {
        aValue = a.account.name.toLowerCase();
        bValue = b.account.name.toLowerCase();
      } else if (sortConfig.key === 'createdAt') {
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
      } else if (sortConfig.key === 'pickupDate' || sortConfig.key === 'fulfillmentDate') {
        aValue = (a as any).fulfillmentDate || a.pickupDate || '9999-12-31';
        bValue = (b as any).fulfillmentDate || b.pickupDate || '9999-12-31';
      } else {
        aValue = a[sortConfig.key as keyof OrderWithDetails];
        bValue = b[sortConfig.key as keyof OrderWithDetails];
        
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredOrders, sortConfig]);

  // Calculate pagination
  const totalPages = Math.ceil(sortedOrders.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedOrders = sortedOrders.slice(startIndex, endIndex);

  // Handle order click
  const handleOrderClick = (order: OrderWithDetails) => {
    setSelectedOrder(order);
    setIsOrderSlipOpen(true);
  };

  // Handle checkbox selection
  const handleOrderSelect = (orderId: number, checked: boolean | string) => {
    const isChecked = checked === true;
    setSelectedOrderIds(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(orderId);
      } else {
        newSet.delete(orderId);
      }
      return newSet;
    });
  };

  // Handle select all checkbox
  const handleSelectAll = (checked: boolean | string) => {
    const isChecked = checked === true;
    if (isChecked) {
      // Select ALL filtered orders, not just current page
      setSelectedOrderIds(new Set(filteredOrders.map(order => order.id)));
    } else {
      setSelectedOrderIds(new Set());
    }
  };

  const isAllSelected = filteredOrders.length > 0 && selectedOrderIds.size === filteredOrders.length;
  const isIndeterminate = selectedOrderIds.size > 0 && selectedOrderIds.size < filteredOrders.length;


  // Handle PDF export
  const handleGeneratePDF = async () => {
    try {
      const selectedCount = selectedOrderIds.size;
      
      // Only allow export if orders are selected
      if (selectedCount === 0) {
        toast({
          title: "No orders selected",
          description: "Please select orders to export.",
          variant: "destructive",
        });
        return;
      }
      
      const orderIds = Array.from(selectedOrderIds);
      console.log(`Exporting ${selectedCount} selected orders to PDF packing slips`);

      await openPickSheetInNewWindow(orderIds);

      toast({
        title: "Pick-sheets ready",
        description: `${orderIds.length} pick-sheet${orderIds.length > 1 ? 's' : ''} opened in new tab.`,
      });
    } catch (error) {
      console.error('PDF export error:', error);
      toast({
        title: "Export failed",
        description: "Failed to generate PDF file. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Delete selected orders mutation
  const deleteOrdersMutation = useMutation({
    // B12/F1: process sequentially and track per-order outcome. The old
    // Promise.all short-circuited on the first failure — onSuccess never ran,
    // so already-deleted orders were stranded in a stale UI (cache not
    // invalidated, still selected). Mirrors handleBatchStatusUpdate.
    mutationFn: async (orderIds: number[]) => {
      let successCount = 0;
      const failedIds: number[] = [];
      for (const orderId of orderIds) {
        try {
          await apiDeleteOrder(orderId);
          successCount++;
        } catch {
          failedIds.push(orderId);
        }
      }
      return { successCount, failedIds, orderIds };
    },
    onSuccess: ({ successCount, failedIds, orderIds }) => {
      // Always refresh from server state, regardless of partial failure.
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      // Drop the ones that actually deleted; keep failed ones selected for retry.
      const failedSet = new Set(failedIds);
      setSelectedOrderIds((prev) => {
        const next = new Set(prev);
        for (const id of orderIds) if (!failedSet.has(id)) next.delete(id);
        return next;
      });
      if (failedIds.length === 0) {
        toast({
          title: "Orders deleted",
          description: `${successCount} order${successCount > 1 ? 's' : ''} deleted successfully.`,
        });
      } else if (successCount > 0) {
        toast({
          title: "Partial delete",
          description: `${successCount} deleted, ${failedIds.length} failed. The failed orders are still selected.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Failed to delete orders",
          description: "Please try again later.",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete orders",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // Open the delete-confirmation dialog for an explicit list of order ids.
  // Callers must pass the targets (no implicit read of selectedOrderIds), so
  // per-row deletes don't have to overwrite selection state to "smuggle" the
  // target into the dialog.
  const requestDelete = (orderIds: number[]) => {
    if (orderIds.length === 0) return;
    setPendingDeleteOrderIds(orderIds);
    setIsDeleteDialogOpen(true);
  };

  // Toolbar bulk-delete entry point — pre-existing API kept for the existing
  // bulk-action `...` menu callsites.
  const handleDelete = () => {
    if (selectedOrderIds.size === 0) return;
    requestDelete(Array.from(selectedOrderIds));
  };

  // Confirm delete action
  const confirmDelete = () => {
    if (pendingDeleteOrderIds.length === 0) {
      setIsDeleteDialogOpen(false);
      return;
    }
    deleteOrdersMutation.mutate(pendingDeleteOrderIds);
    setIsDeleteDialogOpen(false);
    setPendingDeleteOrderIds([]);
  };

  // Handle launch kiosk action - uses dialog approach to avoid popup blockers
  const handleLaunchKiosk = async () => {
    if (!orgSlug) {
      toast({
        title: "Error",
        description: "Organization not found.",
        variant: "destructive",
      });
      return;
    }

    setIsLaunchingKiosk(true);
    try {
      const response = await fetch(`/org/${orgSlug}/api/kiosk/admin-launch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Failed to launch kiosk");
      }

      // Store the kiosk URL and show dialog for user to click
      const kioskUrl = `${window.location.origin}/org/${orgSlug}/kiosk?session=${data.sessionToken}`;
      setKioskLaunchUrl(kioskUrl);
      setShowKioskLaunchDialog(true);

      toast({
        title: "Kiosk ready",
        description: `Session will expire in ${data.timeoutMinutes} minutes.`,
      });
    } catch (error: any) {
      toast({
        title: "Failed to launch kiosk",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLaunchingKiosk(false);
    }
  };
  
  // Open kiosk in new tab - called from dialog button click (synchronous user gesture)
  const openKioskTab = () => {
    window.open(kioskLaunchUrl, '_blank');
    setShowKioskLaunchDialog(false);
  };

  // Handle merge orders action
  const handleMergeOrders = () => {
    const selectedCount = selectedOrderIds.size;
    if (selectedCount < 2) {
      toast({
        title: "Select at least 2 orders",
        description: "You need to select at least 2 orders to merge.",
        variant: "destructive",
      });
      return;
    }

    // Get selected orders and validate they're from the same account
    const selectedOrderData = orders.filter(o => selectedOrderIds.has(o.id));
    const accountIds = new Set(selectedOrderData.map(o => o.accountId));
    
    if (accountIds.size > 1) {
      toast({
        title: "Cannot merge orders",
        description: "All selected orders must be from the same customer account.",
        variant: "destructive",
      });
      return;
    }

    // Check for QB-synced orders
    const qbSyncedOrder = selectedOrderData.find(o => (o as any).quickbooksSyncedAt != null);
    if (qbSyncedOrder) {
      toast({
        title: "Cannot merge orders",
        description: "Orders that have been sent to QuickBooks cannot be merged.",
        variant: "destructive",
      });
      return;
    }

    setIsMergeDialogOpen(true);
  };

  // Confirm merge action
  const confirmMerge = async () => {
    const orderIds = Array.from(selectedOrderIds);
    setIsMerging(true);
    
    try {
      const response = await apiRequest("POST", "/api/orders/merge-orders", { orderIds });
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || "Failed to merge orders");
      }
      
      toast({
        title: "Orders merged",
        description: result.message,
      });
      
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      setSelectedOrderIds(new Set());
    } catch (error: any) {
      toast({
        title: "Failed to merge orders",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsMerging(false);
      setIsMergeDialogOpen(false);
    }
  };

  // Handle QuickBooks action with batch processing
  const handleSendToQuickBooks = async (confirmedResend = false) => {
    const selectedCount = selectedOrderIds.size;
    if (selectedCount === 0) {
      toast({
        title: "No orders selected",
        description: "Please select orders to send to QuickBooks.",
        variant: "destructive",
      });
      return;
    }

    // Get selected orders
    const selectedOrderData = orders.filter(o => selectedOrderIds.has(o.id));

    // QB-26: cancelled orders are never sent. The server skips them (and says
    // so in `skipped`); here we only refuse an all-cancelled selection and warn
    // about a mixed one, so the admin isn't surprised by the skip.
    const cancelledSelected = selectedOrderData.filter(o => o.status === 'cancelled');
    if (cancelledSelected.length === selectedOrderData.length) {
      toast({
        title: "Cancelled orders can't be sent",
        description: "Cancelled orders are never sent to QuickBooks. Select pending or reviewed orders.",
        variant: "destructive",
      });
      return;
    }
    if (cancelledSelected.length > 0 && !confirmedResend) {
      toast({
        title: `${cancelledSelected.length} cancelled ${cancelledSelected.length === 1 ? 'order' : 'orders'} will be skipped`,
        description: "Cancelled orders are never sent to QuickBooks.",
      });
    }

    // Check if any selected orders are already synced to QuickBooks
    const ordersAlreadySent = selectedOrderData.filter(o => o.status !== 'cancelled' && (o as any).quickbooksSyncedAt != null);

    if (ordersAlreadySent.length > 0 && !confirmedResend) {
      // Show confirmation dialog
      setAlreadySentOrders(ordersAlreadySent);
      setShowResendConfirmDialog(true);
      return;
    }

    try {
      setIsExporting(true);
      
      // First check if QuickBooks is authenticated
      console.log('Checking QuickBooks authentication status...');
      const authResponse = await apiRequest("GET", "/api/quickbooks/auth-status");
      const authData = await authResponse.json();
      console.log('QuickBooks auth status:', authData);
      
      if (!authData.isAuthenticated || !authData.hasValidToken) {
        console.log('QuickBooks not authenticated, showing error message');
        toast({
          title: "QuickBooks not connected",
          description: "Please connect to QuickBooks first in Settings to create invoice drafts.",
          variant: "destructive",
        });
        return;
      }

      // Get selected orders
      const selectedOrderData = orders.filter(o => selectedOrderIds.has(o.id));
      const orderIds = selectedOrderData.map(o => o.id);
      
      // Step 1: Validate existing QuickBooks customer links (check for deleted customers)
      setIsCheckingQBMatches(true);

      try {
        const validationResponse = await apiRequest("POST", "/api/quickbooks/validate-customer-links", { orderIds });
        const validation = await validationResponse.json();

        if (!validation.valid && validation.brokenLinks && validation.brokenLinks.length > 0) {
          Sentry.addBreadcrumb({ category: "quickbooks", message: "Broken QB customer links detected (bulk export)", level: "warning", data: { count: validation.brokenLinks.length } });
          setBrokenLinkAccounts(validation.brokenLinks);
          setShowBrokenLinkDialog(true);
          setIsExporting(false);
          setIsCheckingQBMatches(false);
          return; // Wait for dialog confirmation
        }
      } catch (validationError) {
        Sentry.captureException(validationError, { tags: { category: "quickbooks", flow: "validate_links_client_bulk" } });
        console.error("QB customer-link validation failed:", validationError instanceof Error ? validationError.message : String(validationError));
        // Continue anyway - the original flow will handle errors
      }
      
      setIsCheckingQBMatches(false);
      
      // Step 2: Check for unlinked accounts (accounts without any QuickBooks ID)
      // Find unique accounts without QuickBooks IDs - include phone for better matching
      const unlinkedAccounts = new Map<number, { accountId: number; accountName: string; phone?: string }>();
      for (const order of selectedOrderData) {
        if (order.account && !order.account.quickbooksCustomerId) {
          unlinkedAccounts.set(order.account.id, {
            accountId: order.account.id,
            accountName: order.account.name,
            phone: order.account.phone || undefined
          });
        }
      }

      // If there are unlinked accounts, search QuickBooks for matches
      if (unlinkedAccounts.size > 0) {
        setIsCheckingQBMatches(true);
        console.log(`Found ${unlinkedAccounts.size} unlinked accounts, searching for QB matches...`);
        
        const accountsWithMatches: UnlinkedAccountWithMatches[] = [];
        
        for (const account of Array.from(unlinkedAccounts.values())) {
          try {
            // Build search URL with phone for better matching
            let searchUrl = `/api/quickbooks/search-customer?name=${encodeURIComponent(account.accountName)}`;
            if (account.phone) {
              searchUrl += `&phone=${encodeURIComponent(account.phone)}`;
            }
            
            const searchResponse = await apiRequest("GET", searchUrl);
            if (searchResponse.ok) {
              const data = await searchResponse.json();
              accountsWithMatches.push({
                accountId: account.accountId,
                accountName: account.accountName,
                matches: data.matches || []
              });
            } else {
              accountsWithMatches.push({
                accountId: account.accountId,
                accountName: account.accountName,
                matches: []
              });
            }
          } catch (err) {
            Sentry.captureException(err, { tags: { category: "quickbooks", flow: "search_customer" }, extra: { accountId: account.accountId } });
            console.error("QB customer search failed for account", account.accountId, err instanceof Error ? err.message : String(err));
            accountsWithMatches.push({
              accountId: account.accountId,
              accountName: account.accountName,
              matches: []
            });
          }
        }
        
        setIsCheckingQBMatches(false);
        
        // If any accounts have matches, show the dialog
        const accountsWithPotentialMatches = accountsWithMatches.filter(a => a.matches.length > 0);
        if (accountsWithPotentialMatches.length > 0) {
          setUnlinkedAccountsWithMatches(accountsWithMatches);
          setShowQBLinkDialog(true);
          setIsExporting(false);
          return; // Wait for dialog confirmation
        }
        
        // No matches found - proceed with invoice creation (will create new QB customers)
        console.log('No QB matches found for unlinked accounts, proceeding with invoice creation...');
      }

      // Step 3: Check for product multi-matches before proceeding
      await checkProductMatchesAndProceed(selectedOrderData, undefined, confirmedResend);

    } catch (error) {
      console.error("=== QuickBooks export failed ===", error);
      handleQuickBooksError(error);
    } finally {
      console.log('Setting isExporting to false...');
      setIsExporting(false);
      setIsCheckingQBMatches(false);
    }
  };

  // Handle single order send to QuickBooks (from order slip modal)
  const handleSingleOrderSendToQB = async (orderId: number, confirmedResend = false) => {
    // QB-26: a cancelled order is never sendable (the server refuses it too).
    const target = orders.find(o => o.id === orderId);
    if (target && target.status === 'cancelled') {
      toast({
        title: "Cancelled orders can't be sent",
        description: "Cancelled orders are never sent to QuickBooks.",
        variant: "destructive",
      });
      return;
    }

    // Resend guard: if this order is already synced and the user hasn't confirmed,
    // surface the same confirmation dialog the batch path uses. Otherwise the
    // server's 409 ALREADY_SYNCED would arrive as a raw error toast.
    if (!confirmedResend) {
      const existing = orders.find(o => o.id === orderId);
      if (existing && (existing as any).quickbooksSyncedAt != null) {
        setAlreadySentOrders([existing]);
        setPendingSingleResendOrderId(orderId);
        setShowResendConfirmDialog(true);
        return;
      }
    }

    setSingleOrderQBSending(true);

    try {
      // Close the modal first so user sees the progress
      setIsOrderSlipOpen(false);

      // Directly call the QB invoice flow for this single order
      // Don't use handleSendToQuickBooks which reads from state (async update issues)
      setIsExporting(true);

      // First check if QuickBooks is authenticated
      console.log('Checking QuickBooks authentication status...');
      const authResponse = await apiRequest("GET", "/api/quickbooks/auth-status");
      const authData = await authResponse.json();
      console.log('QuickBooks auth status:', authData);

      if (!authData.isAuthenticated || !authData.hasValidToken) {
        console.log('QuickBooks not authenticated, showing error message');
        toast({
          title: "QuickBooks not connected",
          description: "Please connect to QuickBooks first in Settings to create invoice drafts.",
          variant: "destructive",
        });
        return;
      }

      // Get the single order
      const singleOrder = orders.find(o => o.id === orderId);
      if (!singleOrder) {
        toast({
          title: "Order not found",
          description: "Could not find the order to send to QuickBooks.",
          variant: "destructive",
        });
        return;
      }
      
      const selectedOrderData = [singleOrder];
      const orderIds = [orderId];
      
      // Step 1: Validate existing QuickBooks customer links
      setIsCheckingQBMatches(true);

      try {
        const validationResponse = await apiRequest("POST", "/api/quickbooks/validate-customer-links", { orderIds });
        const validation = await validationResponse.json();

        if (!validation.valid && validation.brokenLinks && validation.brokenLinks.length > 0) {
          Sentry.addBreadcrumb({ category: "quickbooks", message: "Broken QB customer links detected (single-order export)", level: "warning", data: { count: validation.brokenLinks.length } });
          setBrokenLinkAccounts(validation.brokenLinks);
          setShowBrokenLinkDialog(true);
          setIsExporting(false);
          setIsCheckingQBMatches(false);
          return; // Wait for dialog confirmation
        }
      } catch (validationError) {
        Sentry.captureException(validationError, { tags: { category: "quickbooks", flow: "validate_links_client_single" } });
        console.error("QB customer-link validation failed:", validationError instanceof Error ? validationError.message : String(validationError));
      }
      
      setIsCheckingQBMatches(false);
      
      // Step 2: Check for unlinked accounts
      const unlinkedAccounts = new Map<number, { accountId: number; accountName: string; phone?: string; email?: string }>();
      for (const order of selectedOrderData) {
        if (order.account && !order.account.quickbooksCustomerId) {
          unlinkedAccounts.set(order.account.id, {
            accountId: order.account.id,
            accountName: order.account.name,
            phone: order.account.phone || undefined
          });
        }
      }

      // If there are unlinked accounts, search QuickBooks for matches
      if (unlinkedAccounts.size > 0) {
        setIsCheckingQBMatches(true);
        console.log(`Found ${unlinkedAccounts.size} unlinked accounts, searching for QB matches...`);
        
        const accountsWithMatches: UnlinkedAccountWithMatches[] = [];
        
        for (const account of Array.from(unlinkedAccounts.values())) {
          try {
            let searchUrl = `/api/quickbooks/search-customer?name=${encodeURIComponent(account.accountName)}`;
            if (account.phone) {
              searchUrl += `&phone=${encodeURIComponent(account.phone)}`;
            }
            
            const searchResponse = await apiRequest("GET", searchUrl);
            const searchResults = await searchResponse.json();
            
            if (searchResults.matches && searchResults.matches.length > 0) {
              accountsWithMatches.push({
                accountId: account.accountId,
                accountName: account.accountName,
                matches: searchResults.matches
              });
            } else {
              accountsWithMatches.push({
                accountId: account.accountId,
                accountName: account.accountName,
                matches: []
              });
            }
          } catch (searchError) {
            Sentry.captureException(searchError, { tags: { category: "quickbooks", flow: "search_customer_single" }, extra: { accountId: account.accountId } });
            console.error("QB customer search failed for account", account.accountId, searchError instanceof Error ? searchError.message : String(searchError));
            accountsWithMatches.push({
              accountId: account.accountId,
              accountName: account.accountName,
              matches: []
            });
          }
        }
        
        setIsCheckingQBMatches(false);
        
        // If any accounts have matches, show the dialog
        const accountsWithPotentialMatches = accountsWithMatches.filter(a => a.matches.length > 0);
        if (accountsWithPotentialMatches.length > 0) {
          // Temporarily set selectedOrderIds for the dialog flow
          setSelectedOrderIds(new Set([orderId]));
          setUnlinkedAccountsWithMatches(accountsWithMatches);
          setShowQBLinkDialog(true);
          setIsExporting(false);
          return; // Wait for dialog confirmation
        }
      }

      // Step 3: Check for product multi-matches before proceeding
      await checkProductMatchesAndProceed(selectedOrderData, undefined, confirmedResend);

    } catch (error) {
      console.error("=== QuickBooks export failed ===", error);
      handleQuickBooksError(error);
    } finally {
      console.log('Setting isExporting to false...');
      setIsExporting(false);
      setIsCheckingQBMatches(false);
      setSingleOrderQBSending(false);
    }
  };

  // Check for product multi-matches before invoice creation
  const checkProductMatchesAndProceed = async (
    selectedOrderData: OrderWithDetails[],
    customerLinkDecisions?: LinkDecision[],
    confirmedResend: boolean = false
  ) => {
    const orderIds = selectedOrderData.map(o => o.id);
    
    try {
      setIsCheckingQBMatches(true);
      console.log('Checking for product multi-matches...');
      const response = await apiRequest("POST", "/api/quickbooks/check-product-matches", { orderIds });
      const data = await response.json();
      
      if (data.requireAuth) {
        toast({
          title: "QuickBooks not connected",
          description: "Please reconnect to QuickBooks to create invoice drafts.",
          variant: "destructive",
        });
        setIsExporting(false);
        setIsCheckingQBMatches(false);
        return;
      }
      
      if (data.needsResolution && data.multiMatches.length > 0) {
        console.log(`Found ${data.multiMatches.length} products with multiple QB matches`);
        setUnlinkedProductsWithMatches(data.multiMatches);
        setPendingOrderDataForProductLink(selectedOrderData);
        setPendingCustomerLinkDecisions(customerLinkDecisions);
        setPendingConfirmedResend(confirmedResend);
        setShowQBProductLinkDialog(true);
        setIsExporting(false);
        setIsCheckingQBMatches(false);
        return; // Wait for dialog confirmation
      }
    } catch (err) {
      console.error('Failed to check product matches, continuing anyway:', err);
    }

    setIsCheckingQBMatches(false);

    // No product matches need resolution - proceed with invoice creation
    await executeQuickBooksInvoiceCreation(selectedOrderData, customerLinkDecisions, undefined, confirmedResend);
  };

  // Handle QB product link dialog confirmation
  const handleQBProductLinkConfirm = async (decisions: ProductLinkDecision[]) => {
    try {
      setIsExporting(true);
      setShowQBProductLinkDialog(false);
      
      await executeQuickBooksInvoiceCreation(
        pendingOrderDataForProductLink,
        pendingCustomerLinkDecisions,
        decisions,
        pendingConfirmedResend
      );
      setPendingConfirmedResend(false);
    } catch (error) {
      console.error("Failed to process product link decisions:", error);
      toast({
        title: "Failed to send invoices",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
      setIsExporting(false);
    }
  };

  // Handle QB link dialog confirmation
  const handleQBLinkConfirm = async (decisions: LinkDecision[]) => {
    try {
      setIsExporting(true);
      setPendingLinkDecisions(decisions);
      
      // Build a map of accountId -> qbCustomerId for quick lookup
      const linkMap = new Map<number, string>();
      
      // First, link accounts that user chose to link to existing QB customers
      const linkPromises = decisions
        .filter(d => d.decision === 'link' && d.qbCustomerId)
        .map(d => {
          linkMap.set(d.accountId, d.qbCustomerId!);
          return apiRequest("PATCH", `/api/accounts/${d.accountId}`, {
            quickbooksCustomerId: d.qbCustomerId
          });
        });
      
      if (linkPromises.length > 0) {
        console.log(`Linking ${linkPromises.length} accounts to existing QB customers...`);
        await Promise.all(linkPromises);
        // Invalidate accounts cache so the linked IDs are available
        queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      }
      
      // Close dialog and proceed with product match checking, then invoice creation
      setShowQBLinkDialog(false);
      
      // Get selected orders and update their account QB IDs with linked values
      const selectedOrderData = orders.filter(o => selectedOrderIds.has(o.id)).map(order => {
        // If this account was linked in this session, use the linked QB ID
        if (order.account && linkMap.has(order.account.id)) {
          return {
            ...order,
            account: {
              ...order.account,
              quickbooksCustomerId: linkMap.get(order.account.id)!
            }
          };
        }
        return order;
      });
      
      await checkProductMatchesAndProceed(selectedOrderData, decisions);
      
    } catch (error) {
      console.error("Failed to link accounts:", error);
      toast({
        title: "Linking failed",
        description: "Failed to link accounts to QuickBooks. Please try again.",
        variant: "destructive",
      });
      setIsExporting(false);
    }
  };

  // Handle broken link dialog confirmation (for customers deleted from QuickBooks)
  const handleBrokenLinkConfirm = async (decisions: LinkDecision[]) => {
    try {
      setIsExporting(true);
      setBrokenLinkConfirming(true); // Prevent cancel toast when closing dialog
      
      // Build a map of accountId -> qbCustomerId for quick lookup
      const linkMap = new Map<number, string | null>();
      
      // Process decisions - link to new customer or clear the broken link
      const updatePromises = decisions.map(d => {
        if (d.decision === 'link' && d.qbCustomerId) {
          // Link to a different existing QB customer
          linkMap.set(d.accountId, d.qbCustomerId);
          return apiRequest("PATCH", `/api/accounts/${d.accountId}`, {
            quickbooksCustomerId: d.qbCustomerId
          });
        } else {
          // Clear the broken link - will create new customer in QB
          linkMap.set(d.accountId, null);
          return apiRequest("PATCH", `/api/accounts/${d.accountId}`, {
            quickbooksCustomerId: null
          });
        }
      });
      
      console.log(`Processing ${updatePromises.length} broken link decisions...`);
      await Promise.all(updatePromises);
      
      // Invalidate accounts cache
      queryClient.invalidateQueries({ queryKey: ["/api/accounts"] });
      
      // Close dialog
      setShowBrokenLinkDialog(false);
      setBrokenLinkAccounts([]);
      setBrokenLinkConfirming(false);
      
      // Get selected orders and update their account QB IDs with new values
      const selectedOrderData = orders.filter(o => selectedOrderIds.has(o.id)).map(order => {
        if (order.account && linkMap.has(order.account.id)) {
          const newQbId = linkMap.get(order.account.id);
          return {
            ...order,
            account: {
              ...order.account,
              quickbooksCustomerId: newQbId ?? null
            }
          };
        }
        return order;
      });
      
      // Build a set of accounts that the user explicitly chose to "create_new" in the broken link dialog
      // These should skip the unlinked matching flow since the user already made that decision
      const createNewAccountIds = new Set(
        decisions.filter(d => d.decision === 'create_new').map(d => d.accountId)
      );
      
      // Now check for any accounts that still don't have QuickBooks IDs 
      // BUT exclude accounts where user already chose "create_new" - they already made their decision
      const unlinkedAccounts = new Map<number, { accountId: number; accountName: string }>();
      for (const order of selectedOrderData) {
        if (order.account && !order.account.quickbooksCustomerId && !createNewAccountIds.has(order.account.id)) {
          unlinkedAccounts.set(order.account.id, {
            accountId: order.account.id,
            accountName: order.account.name
          });
        }
      }
      
      // If there are unlinked accounts (that weren't already resolved), search QuickBooks for matches
      if (unlinkedAccounts.size > 0) {
        setIsCheckingQBMatches(true);
        console.log(`Found ${unlinkedAccounts.size} unlinked accounts after fixing broken links, searching for QB matches...`);
        
        const accountsWithMatches: UnlinkedAccountWithMatches[] = [];
        
        for (const account of Array.from(unlinkedAccounts.values())) {
          try {
            const searchResponse = await apiRequest("GET", `/api/quickbooks/search-customer?name=${encodeURIComponent(account.accountName)}`);
            if (searchResponse.ok) {
              const data = await searchResponse.json();
              accountsWithMatches.push({
                accountId: account.accountId,
                accountName: account.accountName,
                matches: data.matches || []
              });
            } else {
              accountsWithMatches.push({
                accountId: account.accountId,
                accountName: account.accountName,
                matches: []
              });
            }
          } catch (err) {
            Sentry.captureException(err, { tags: { category: "quickbooks", flow: "search_customer" }, extra: { accountId: account.accountId } });
            console.error("QB customer search failed for account", account.accountId, err instanceof Error ? err.message : String(err));
            accountsWithMatches.push({
              accountId: account.accountId,
              accountName: account.accountName,
              matches: []
            });
          }
        }
        
        setIsCheckingQBMatches(false);
        
        // If any accounts have matches, show the dialog
        const accountsWithPotentialMatches = accountsWithMatches.filter(a => a.matches.length > 0);
        if (accountsWithPotentialMatches.length > 0) {
          setUnlinkedAccountsWithMatches(accountsWithMatches);
          setShowQBLinkDialog(true);
          setIsExporting(false);
          return; // Wait for dialog confirmation
        }
        
        // No matches found - proceed with invoice creation (will create new QB customers)
        console.log('No QB matches found for unlinked accounts, proceeding with invoice creation...');
      }
      
      // Continue with product match checking, then invoice creation
      await checkProductMatchesAndProceed(selectedOrderData);
      
    } catch (error) {
      console.error("Failed to fix broken links:", error);
      toast({
        title: "Failed to update customer links",
        description: "Please try again.",
        variant: "destructive",
      });
      setIsExporting(false);
      setIsCheckingQBMatches(false);
      setBrokenLinkConfirming(false);
    }
  };

  // Execute the actual QuickBooks invoice creation
  const executeQuickBooksInvoiceCreation = async (
    selectedOrderData: OrderWithDetails[],
    linkDecisions?: LinkDecision[],
    productLinkDecisions?: ProductLinkDecision[],
    confirmedResend: boolean = false
  ) => {
    const totalItems = selectedOrderData.reduce((sum, order) => sum + (order.items?.length || 0), 0);
    
    // Show progress modal
    setProcessingProgress({
      show: true,
      orderId: selectedOrderData[0]?.id,
      customerName: selectedOrderData[0]?.account?.name,
      totalItems: totalItems,
      processedItems: 0,
      status: 'initializing',
      currentItem: 'Starting QuickBooks export...'
    });

    const orderIdsToSend = selectedOrderData.length > 0 
      ? selectedOrderData.map(o => o.id) 
      : Array.from(selectedOrderIds);
    
    let response: Response;
    try {
      response = await apiRequest("POST", "/api/quickbooks/create-invoices", {
        orderIds: orderIdsToSend,
        linkDecisions: linkDecisions,
        productLinkDecisions: productLinkDecisions,
        confirmedResend: confirmedResend
      });
    } finally {
      // The send marks orders synced (and pending → reviewed) server-side, so the
      // table and the summary strip are stale the moment it settles — also when it
      // is refused: a 409 ALREADY_SYNCED means the row on screen is already wrong.
      refreshAfterQuickbooksSend(queryClient);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Update progress modal with error
      setProcessingProgress(prev => ({
        ...prev,
        status: 'failed',
        error: errorData.message || 'Failed to create invoice drafts'
      }));
      
      if (errorData.requireAuth) {
        toast({
          title: "QuickBooks authentication required",
          description: "Please reconnect to QuickBooks to create invoice drafts.",
          variant: "destructive",
        });
        
        // Close progress modal after 3 seconds
        setTimeout(() => setProcessingProgress({ show: false }), 3000);
        return;
      }
      
      // Close progress modal after 3 seconds
      setTimeout(() => setProcessingProgress({ show: false }), 3000);
      throw new Error(errorData.message || 'Failed to create invoice drafts');
    }

    const result = await response.json();

    const report = summarizeQuickbooksSend(result.results);

    if (report.notSent.length > 0) {
      // The progress modal's "completed" state says an invoice was created —
      // not true for every order here. The send report says what happened.
      setProcessingProgress({ show: false });
    } else {
      // Update progress modal with success
      setProcessingProgress(prev => ({
        ...prev,
        status: 'completed',
        processedItems: prev.totalItems,
        currentItem: 'Invoice creation completed!'
      }));

      // Close progress modal after 2 seconds
      setTimeout(() => setProcessingProgress({ show: false }), 2000);
    }

    // A linked item named otherwise than its product is the tenant's choice more often than
    // not (Gomes invoices count-specific products as one generic item), so that list alone
    // never opens the dialog; it still shows when the dialog opens for a refused send or a
    // placeholder price. Narrowing the warning itself: chip task_ace2267b.
    if (report.notSent.length > 0 || report.placeholderPriced.length > 0) {
      setSendReport(report);
    }

    const nothingSent = result.summary.successful === 0 && result.summary.failed > 0;
    toast({
      title: nothingSent ? "No invoices were sent" : "Invoice drafts created",
      variant: nothingSent ? "destructive" : undefined,
      description: `${result.summary.successful} invoice drafts created successfully in QuickBooks${result.summary.failed > 0 ? `. ${result.summary.failed} failed.` : '.'}${result.summary.skipped > 0 ? ` ${result.summary.skipped} skipped (${Array.from(new Set((result.skipped ?? []).map((s: { reason: string }) => s.reason.replace(/_/g, ' ')))).join(', ')}).` : ''}`,
    });

    // Log detailed results for debugging
    console.log('QuickBooks invoice creation results:', result);
    console.log('Results breakdown:', JSON.stringify(result, null, 2));
    
    // Log specific failures
    if (result.results && Array.isArray(result.results)) {
      result.results.forEach((r: any) => {
        if (!r.success) {
          Sentry.captureException(new Error(r.error || "Invoice creation failed"), {
            tags: { category: "quickbooks", flow: "invoice_create" },
            extra: { orderId: r.orderId },
          });
          console.error("Invoice creation failed:", r.orderId, r.error);
        }
      });
    }
    
    // Clear selection after successful export
    setSelectedOrderIds(new Set());
    setIsExporting(false);
  };

  // Handle QuickBooks error
  const handleQuickBooksError = (error: unknown) => {
    console.error("Error details:", {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });
    
    // Update progress modal with error
    setProcessingProgress(prev => ({
      ...prev,
      status: 'failed',
      error: error instanceof Error ? error.message : 'Failed to create invoice drafts'
    }));
    
    // Close progress modal after 3 seconds
    setTimeout(() => setProcessingProgress({ show: false }), 3000);
    
    toast({
      title: "Export failed",
      description: error instanceof Error ? error.message : "Failed to create invoice drafts in QuickBooks",
      variant: "destructive",
    });
  };

  // Generate Excel export


  // Show loading states (impersonation verification or data loading)
  if (isImpersonationLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <span className="ml-2 text-muted-foreground">Verifying access...</span>
      </div>
    );
  }

  if (statsLoading || ordersLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <>
      {/* Support Mode Banner - shown when Super Admin is impersonating */}
      {isImpersonating && (
        <SupportModeBanner orgName={impersonatedOrgName} onExit={handleExitSupportMode} />
      )}
      
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">

      {/* Phase 4c: summary strip — a non-clickable one-line readout where the
          KPI card band used to be. Reflects ALL active filters; counts are
          computed over filteredOrders. The truncation indicator surfaces the
          200-row server cap so an unfiltered list doesn't silently appear
          complete when it isn't (true server-side pagination is a separate
          future effort — see brief Phase 4c §5). */}
      <div
        className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5 text-sm sm:-mb-2"
        data-testid="orders-summary-strip"
        role="status"
        aria-live="polite"
      >
        {/* Lead figure: the count that answers "how much is in front of me".
            When chips narrow the loaded window it becomes "12 of 87" with a
            filter glyph — the affordance that the numbers are scoped, without
            making the strip itself a control. */}
        <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
          <span
            className={`text-base font-semibold tracking-tight tabular-nums ${summary.total === 0 ? "text-muted-foreground" : "text-foreground"}`}
            data-testid="orders-summary-total"
          >
            {countFormatter.format(summary.total)}
          </span>
          {summary.narrowed && (
            <span className="text-muted-foreground tabular-nums" data-testid="orders-summary-loaded">
              of {countFormatter.format(summary.loaded)}
            </span>
          )}
          <span className="text-muted-foreground">{summary.total === 1 && !summary.narrowed ? "order" : "orders"}</span>
          {summary.narrowed && (
            <ListFilter
              className="h-3.5 w-3.5 self-center text-muted-foreground"
              role="img"
              aria-label="Counts reflect the active filters"
              data-testid="orders-summary-filtered"
            />
          )}
        </span>

        {summaryEmptyReason ? (
          <span className="text-muted-foreground basis-full sm:basis-auto sm:border-l sm:border-border sm:pl-4" data-testid="orders-summary-empty">
            {summaryEmptyReason}
          </span>
        ) : (
          /* Breakdown. Desktop: inline after a hairline rule. Phone: drops to
             its own row as three grid tracks so the segments align across
             renders instead of wrapping ragged. The width comes from the
             tracks, never from a bg-white box (see index.css <640px rule). */
          <span className="grid grid-cols-3 gap-x-3 gap-y-1 basis-full sm:flex sm:basis-auto sm:items-baseline sm:gap-x-4 sm:border-l sm:border-border sm:pl-4">
            <SummaryCount value={summary.pending} label="pending" testId="orders-summary-pending" />
            <SummaryCount value={summary.reviewed} label="reviewed" testId="orders-summary-reviewed" />
            <SummaryCount value={summary.sentToQb} label="sent to QB" testId="orders-summary-sent" />
            {summary.cancelled > 0 && (
              <SummaryCount value={summary.cancelled} label="cancelled" testId="orders-summary-cancelled" />
            )}
          </span>
        )}

        {summary.truncated && (
          <span
            className="inline-flex items-center gap-1.5 basis-full sm:basis-auto text-amber-700 dark:text-amber-400"
            data-testid="orders-summary-truncated"
          >
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Showing first 200 — add a filter to narrow.
          </span>
        )}
      </div>

      {/* Orders Table with Unified Filter Toolbar */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="border-b border-gray-100 bg-gray-50/50 py-3 px-4">
          {/* Mobile layout: row 1 = icon buttons right-aligned, row 2 = dropdowns 50/50 */}
          <div className="md:hidden flex flex-col gap-2">
            <div className="flex items-center justify-end gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 bg-white hover:bg-gray-100 border border-gray-200"
                    data-testid="button-actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size === 0 ? undefined : handleGeneratePDF}
                    className={selectedOrderIds.size === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size === 0}
                    data-testid="action-export-pdf"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Print Pick-Sheets
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size === 0 ? undefined : handleDelete}
                    className={selectedOrderIds.size === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size === 0}
                    data-testid="action-delete"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size < 2 ? undefined : handleMergeOrders}
                    className={selectedOrderIds.size < 2 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size < 2}
                    data-testid="action-merge"
                  >
                    <Merge className="mr-2 h-4 w-4" />
                    Merge Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size === 0 || isExporting ? undefined : () => handleSendToQuickBooks()}
                    className={(selectedOrderIds.size === 0 || isExporting) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size === 0 || isExporting}
                    data-testid="action-quickbooks"
                  >
                    {isExporting ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Send to QuickBooks
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      className={selectedOrderIds.size === 0 ? "opacity-50" : ""}
                      disabled={selectedOrderIds.size === 0}
                      data-testid="action-set-status"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Set Status
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => handleBatchStatusUpdate('pending')}
                        className="cursor-pointer"
                        data-testid="action-set-pending"
                      >
                        <Clock className="mr-2 h-4 w-4 text-blue-600" />
                        Pending
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleBatchStatusUpdate('reviewed')}
                        className="cursor-pointer"
                        data-testid="action-set-reviewed"
                      >
                        <Eye className="mr-2 h-4 w-4 text-orange-600" />
                        Reviewed
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                size="icon"
                className="h-8 w-8 p-0 bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green text-white rounded-md flex-shrink-0"
                onClick={() => setIsAdminOrderModalOpen(true)}
                data-testid="button-new-order"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

          </div>

          {/* Desktop layout: single row (unchanged) */}
          <div className="hidden md:flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 bg-white hover:bg-gray-100 border border-gray-200"
                    data-testid="button-actions"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size === 0 ? undefined : handleGeneratePDF}
                    className={selectedOrderIds.size === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size === 0}
                    data-testid="action-export-pdf"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Print Pick-Sheets
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size === 0 ? undefined : handleDelete}
                    className={selectedOrderIds.size === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size === 0}
                    data-testid="action-delete"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size < 2 ? undefined : handleMergeOrders}
                    className={selectedOrderIds.size < 2 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size < 2}
                    data-testid="action-merge"
                  >
                    <Merge className="mr-2 h-4 w-4" />
                    Merge Orders
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={selectedOrderIds.size === 0 || isExporting ? undefined : () => handleSendToQuickBooks()}
                    className={(selectedOrderIds.size === 0 || isExporting) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                    disabled={selectedOrderIds.size === 0 || isExporting}
                    data-testid="action-quickbooks"
                  >
                    {isExporting ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Send to QuickBooks
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      className={selectedOrderIds.size === 0 ? "opacity-50" : ""}
                      disabled={selectedOrderIds.size === 0}
                      data-testid="action-set-status"
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Set Status
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuItem
                        onClick={() => handleBatchStatusUpdate('pending')}
                        className="cursor-pointer"
                        data-testid="action-set-pending"
                      >
                        <Clock className="mr-2 h-4 w-4 text-blue-600" />
                        Pending
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleBatchStatusUpdate('reviewed')}
                        className="cursor-pointer"
                        data-testid="action-set-reviewed"
                      >
                        <Eye className="mr-2 h-4 w-4 text-orange-600" />
                        Reviewed
                      </DropdownMenuItem>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </DropdownMenuContent>
              </DropdownMenu>

            </div>

            <Button
              size="icon"
              className="h-8 w-8 p-0 bg-cn-brand-green hover:bg-[hsl(140,27%,88%)] hover:text-cn-brand-green text-white rounded-md flex-shrink-0"
              onClick={() => setIsAdminOrderModalOpen(true)}
              data-testid="button-new-order"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          {/* Phase 4b: chip-based filter toolbar — Add filter button + active
              chips + Clear all. Replaces the KPI card band, both date/
              fulfillment-type dropdowns, and the half-merged chip row. */}
          <div className="mt-2.5">
            <OrdersFilterToolbar
              filterState={filterState}
              onChange={setFilterState}
              showFulfillmentTypes={deliveryEnabled && pickupEnabled}
              showOrderWindow={showFulfillmentColumn}
              showKioskSource={organization?.kioskEnabled === true || orders.some(o => o.source === "kiosk")}
              availableWindows={availableFulfillmentDates.dates}
              unscheduledCount={availableFulfillmentDates.unscheduledCount}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-9">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                      className="h-4 w-4"
                      data-testid="checkbox-select-all"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('id')}
                      className="flex items-center hover:text-cn-brand-blue"
                    >
                      ORDER ID
                      {sortConfig?.key === 'id' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('account.name')}
                      className="flex items-center hover:text-cn-brand-blue"
                    >
                      ACCOUNT
                      {sortConfig?.key === 'account.name' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('status')}
                      className="flex items-center hover:text-cn-brand-blue"
                    >
                      STATUS
                      {sortConfig?.key === 'status' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('totalItems')}
                      className="flex items-center hover:text-cn-brand-blue"
                    >
                      ITEMS
                      {sortConfig?.key === 'totalItems' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </th>
                  {showFulfillmentColumn && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('fulfillmentDate')}
                      className="flex items-center hover:text-cn-brand-blue"
                    >
                      FULFILLMENT
                      {(sortConfig?.key === 'fulfillmentDate' || sortConfig?.key === 'pickupDate') && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PHONE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="flex items-center hover:text-cn-brand-blue"
                    >
                      ORDERED
                      {sortConfig?.key === 'createdAt' && (
                        sortConfig.direction === 'asc' ? <ChevronUp className="ml-1 h-3 w-3" /> : <ChevronDown className="ml-1 h-3 w-3" />
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedOrders.map((order) => (
                  <ContextMenu key={order.id}>
                    <ContextMenuTrigger asChild>
                      <tr
                        className={`hover:bg-gray-50 transition-opacity duration-300 ${
                          fadingOrderIds.has(order.id) ? "opacity-0" : "opacity-100"
                        }`}
                      >
                    <td className="px-2 py-4 whitespace-nowrap w-9">
                      <Checkbox
                        checked={selectedOrderIds.has(order.id)}
                        onCheckedChange={(checked) => handleOrderSelect(order.id, checked)}
                        className="h-4 w-4"
                        data-testid={`checkbox-order-${order.id}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-dark-grey">
                      <button
                        onClick={() => handleOrderClick(order)}
                        className="text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer"
                      >
                        #ORD-{order.id.toString().padStart(3, '0')}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setLocation(`${adminBase}/accounts/${order.account.id}`)}
                        className="text-cn-brand-blue hover:text-cn-brand-blue-hover hover:underline cursor-pointer"
                      >
                        {order.account.name}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-nowrap items-center gap-1.5">
                      <Popover open={statusPopoverOpen === order.id} onOpenChange={(open) => setStatusPopoverOpen(open ? order.id : null)}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`${
                              order.status === "reviewed" ? "bg-orange-100 text-[#ea580b] hover:bg-orange-200" :
                              order.status === "cancelled" ? "bg-red-100 text-red-800 hover:bg-red-200" :
                              "bg-blue-200 text-cn-brand-blue hover:bg-blue-300"
                            } p-1 h-auto text-xs cursor-pointer`}
                          >
                            {order.status}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-48 p-2"
                          onPointerDownOutside={(e) => {
                            e.stopPropagation();
                          }}
                          onInteractOutside={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <div className="space-y-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs"
                              onClick={() => handleStatusUpdate(order.id, "pending")}
                              disabled={order.status === "pending"}
                            >
                              Mark as Pending
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start text-xs"
                              onClick={() => handleStatusUpdate(order.id, "reviewed")}
                              disabled={order.status === "reviewed"}
                            >
                              Mark as Reviewed
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                      <QbSyncedMark syncedAt={(order as any).quickbooksSyncedAt} className="flex-shrink-0" />
                      <OrderSourceMark order={order} className="flex-shrink-0" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-grey">{order.totalItems}</td>
                    {showFulfillmentColumn && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1.5">
                        {(order as any).fulfillmentType && (
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                            (order as any).fulfillmentType === "delivery"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-green-100 text-green-700"
                          }`}>
                            {(order as any).fulfillmentType === "delivery" ? "Del" : "Pick"}
                          </span>
                        )}
                        {(() => {
                          const fDate = (order as any).fulfillmentDate || order.pickupDate;
                          if (!fDate) return <span className="text-gray-400 italic">—</span>;
                          const today = new Date();
                          const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
                          const isToday = fDate === todayStr;
                          const isTomorrow = fDate === tomorrowStr;
                          const d = new Date(fDate + 'T12:00:00');
                          const label = isToday ? 'Today' : isTomorrow ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                          return (
                            <span className={`font-medium ${isToday ? 'text-cn-brand-blue' : isTomorrow ? 'text-orange-600' : 'text-gray-700'}`}>
                              {label}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{order.account.phone}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                      </tr>
                    </ContextMenuTrigger>
                    <ContextMenuContent className="w-48">
                      <OrderActionItems
                        order={order}
                        variant="context"
                        onView={handleOrderClick}
                        onSendToQuickBooks={handleSingleOrderSendToQB}
                        onRequestDelete={requestDelete}
                      />
                    </ContextMenuContent>
                  </ContextMenu>
                ))}
              </tbody>
            </table>
          </div>
          
          {sortedOrders.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No orders found matching your criteria.
            </div>
          )}
          
        </CardContent>
        
        {/* Pagination Controls */}
        {sortedOrders.length > 0 && (
          <div className="px-6 py-3 border-t border-border-grey">
            <div className="flex items-center justify-between">
              {/* Rows per page only */}
              <div className="flex items-center">
                <Select value={rowsPerPage.toString()} onValueChange={(value) => handleRowsPerPageChange(parseInt(value))}>
                  <SelectTrigger className="w-16 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>
      {/* Order Slip Modal */}
      <OrderSlip
        order={selectedOrder}
        isOpen={isOrderSlipOpen}
        onClose={() => setIsOrderSlipOpen(false)}
        onSendToQuickBooks={handleSingleOrderSendToQB}
        isSendingToQB={singleOrderQBSending}
      />
      {/* Edit Account Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-xs sm:!max-w-md mx-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Edit Account</DialogTitle>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(handleSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))} className="space-y-4">
            <div>
              <Label htmlFor="name">Account Name</Label>
              <Input
                id="name"
                {...form.register("name")}
                placeholder="Customer name or business"
              />
              {form.formState.errors.name && (
                <p className="text-sm text-red-600">{form.formState.errors.name.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                {...form.register("phone")}
                placeholder="(555) 123-4567"
              />
              {form.formState.errors.phone && (
                <p className="text-sm text-red-600">{form.formState.errors.phone.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="customUrl">Custom URL</Label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">/</span>
                <Input
                  id="customUrl"
                  {...form.register("customUrl")}
                  placeholder="custom-url"
                  readOnly
                  className="bg-gray-50"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Auto-generated from account name
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                {...form.register("isActive")}
                className="rounded border-gray-300 text-cn-brand-blue focus:ring-cn-brand-blue"
              />
              <Label htmlFor="isActive">Account is active</Label>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={form.formState.isSubmitting}
                className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover"
              >
                {form.formState.isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => {
          setIsDeleteDialogOpen(open);
          if (!open) setPendingDeleteOrderIds([]);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingDeleteOrderIds.length === 1 ? 'Delete Order' : 'Delete Orders'}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeleteOrderIds.length === 1
                ? `Are you sure you want to delete order #ORD-${pendingDeleteOrderIds[0].toString().padStart(3, '0')}? This action cannot be undone.`
                : `Are you sure you want to delete ${pendingDeleteOrderIds.length} selected orders? This action cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setPendingDeleteOrderIds([]);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteOrdersMutation.isPending}
            >
              {deleteOrdersMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Merge Orders Confirmation Dialog */}
      <AlertDialog open={isMergeDialogOpen} onOpenChange={setIsMergeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge Orders</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const selectedOrderData = orders.filter(o => selectedOrderIds.has(o.id));
                const sortedOrders = [...selectedOrderData].sort((a, b) => a.id - b.id);
                const targetOrder = sortedOrders[0];
                const accountName = targetOrder?.account?.name || 'Unknown';
                
                return (
                  <>
                    <p className="mb-2">
                      You are about to merge <strong>{selectedOrderIds.size} orders</strong> from <strong>{accountName}</strong>.
                    </p>
                    <p className="mb-2">
                      All items and quantities will be combined into the oldest order (ORD-{String(targetOrder?.id || 0).padStart(3, '0')}).
                      The other {selectedOrderIds.size - 1} order{selectedOrderIds.size > 2 ? 's' : ''} will be deleted.
                    </p>
                    <p className="text-amber-600 font-medium">
                      This action cannot be undone.
                    </p>
                  </>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsMergeDialogOpen(false)} disabled={isMerging}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmMerge} 
              className="bg-cn-brand-blue hover:bg-cn-brand-blue-hover"
              disabled={isMerging}
            >
              {isMerging ? "Merging..." : "Merge Orders"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Resend to QuickBooks Confirmation Dialog */}
      <AlertDialog open={showResendConfirmDialog} onOpenChange={setShowResendConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-amber-600">Resend to QuickBooks?</AlertDialogTitle>
            <AlertDialogDescription>
              <p className="mb-3">
                {alreadySentOrders.length === 1 ? (
                  <>The selected order <strong>#{String(alreadySentOrders[0]?.id || 0).padStart(3, '0')}</strong> has already been sent to QuickBooks.</>
                ) : (
                  <>{alreadySentOrders.length} of your selected orders have already been sent to QuickBooks.</>
                )}
              </p>
              <p className="mb-3 font-medium text-amber-700">
                Sending again may create duplicate invoices in QuickBooks.
              </p>
              <p className="text-sm text-gray-600">
                Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowResendConfirmDialog(false);
              setAlreadySentOrders([]);
              setPendingSingleResendOrderId(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowResendConfirmDialog(false);
                // Single-order resend (from lightbox) vs batch resend (from orders page).
                if (pendingSingleResendOrderId != null) {
                  const singleId = pendingSingleResendOrderId;
                  setPendingSingleResendOrderId(null);
                  handleSingleOrderSendToQB(singleId, true);
                } else {
                  handleSendToQuickBooks(true);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Send Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* After a QuickBooks send: what was not sent, what needs a price, and lines on a differently named linked item */}
      <AlertDialog open={sendReport !== null}>
        <AlertDialogContent className="sm:max-w-lg" data-testid="qb-send-report-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {sendReport && sendReport.notSent.length > 0
                ? (sendReport.notSent.length === 1 ? "1 order was not sent to QuickBooks" : `${sendReport.notSent.length} orders were not sent to QuickBooks`)
                : sendReport && sendReport.placeholderPriced.length > 0
                  ? "Some lines need a price in QuickBooks"
                  : "Check the QuickBooks product on some lines"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {sendReport && sendReport.notSent.length > 0
                ? "No invoice was created for the orders listed as not sent. Every other order you selected was sent."
                : sendReport && sendReport.placeholderPriced.length > 0
                  ? "These invoices were created. The lines below had no price, so they were sent at $1.00."
                  : "These invoices were created. Some lines went out as a QuickBooks product with a different name."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[50vh] space-y-4 overflow-y-auto text-sm">
            {sendReport && sendReport.notSent.length > 0 && (
              <section data-testid="qb-send-report-not-sent">
                <div className="mb-2 text-sm font-semibold text-red-700">Not sent</div>
                <ul className="space-y-2">
                  {sendReport.notSent.map((order) => (
                    <li key={order.orderId}>
                      <div className="font-medium text-foreground">
                        {order.customerName || "Order"} <span className="font-normal text-muted-foreground">· Order #{order.orderId}</span>
                      </div>
                      <div className="text-muted-foreground">{order.reason}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {sendReport && sendReport.placeholderPriced.length > 0 && (
              <section data-testid="qb-send-report-placeholder">
                <div className="mb-2 text-sm font-semibold text-foreground">Sent at $1.00 — set the price in QuickBooks</div>
                <ul className="space-y-2">
                  {sendReport.placeholderPriced.map((order) => (
                    <li key={order.orderId}>
                      <div className="font-medium text-foreground">
                        {order.customerName || "Order"}{" "}
                        <span className="font-normal text-muted-foreground">
                          · {order.invoiceNumber ? `Invoice #${order.invoiceNumber}` : `Order #${order.orderId}`}
                        </span>
                      </div>
                      <div className="text-muted-foreground">{order.products.join(", ")}</div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {sendReport && sendReport.linkedItemMismatch.length > 0 && (
              <section data-testid="qb-send-report-linked-item">
                <div className="mb-2 text-sm font-semibold text-foreground">Sent as a QuickBooks product with another name</div>
                <p className="mb-2 text-muted-foreground">
                  These products are linked to a QuickBooks product named differently. If it is the wrong one, change the line in QuickBooks and fix the product's link in Settings.
                </p>
                <ul className="space-y-2">
                  {sendReport.linkedItemMismatch.map((order) => (
                    <li key={order.orderId}>
                      <div className="font-medium text-foreground">
                        {order.customerName || "Order"}{" "}
                        <span className="font-normal text-muted-foreground">
                          · {order.invoiceNumber ? `Invoice #${order.invoiceNumber}` : `Order #${order.orderId}`}
                        </span>
                      </div>
                      <ul className="text-muted-foreground">
                        {order.pairs.map((pair) => (
                          <li key={`${pair.productName}:${pair.qbItemName}`}>
                            {pair.productName} went out as “{pair.qbItemName}”
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setSendReport(null)}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* QuickBooks Batch Processing Progress Modal */}
      <AlertDialog open={processingProgress.show}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {processingProgress.status === 'failed' ? 'Processing Failed' : 
               processingProgress.status === 'completed' ? 'Processing Complete' : 
               'Processing QuickBooks Export'}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-4 pt-4">
              {processingProgress.status === 'failed' ? (
                <div className="space-y-2">
                  <div className="text-red-600 font-medium">
                    {processingProgress.error || 'An error occurred during processing'}
                  </div>
                  <p className="text-sm text-gray-600">
                    Please check the error message and try again.
                  </p>
                </div>
              ) : processingProgress.status === 'completed' ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Invoice created successfully!</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    The invoice draft has been created in QuickBooks.
                  </p>
                </div>
              ) : (
                <>
                  {processingProgress.customerName && (
                    <div className="text-sm">
                      <span className="font-medium">Customer:</span> {processingProgress.customerName}
                    </div>
                  )}
                  
                  {processingProgress.totalItems && processingProgress.totalItems > 0 && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Processing items</span>
                        <span className="font-medium">
                          {processingProgress.processedItems || 0} / {processingProgress.totalItems}
                        </span>
                      </div>
                      <Progress 
                        value={(processingProgress.processedItems || 0) / processingProgress.totalItems * 100} 
                        className="h-2"
                      />
                    </div>
                  )}
                  
                  {processingProgress.currentItem && (
                    <div className="text-sm text-gray-600 flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{processingProgress.currentItem}</span>
                    </div>
                  )}
                  
                  <div className="pt-2 text-sm text-gray-500">
                    Please don't navigate away from this page while processing...
                  </div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Kiosk Launch Dialog - avoids popup blockers by using button click for window.open */}
      <Dialog open={showKioskLaunchDialog} onOpenChange={setShowKioskLaunchDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tablet className="h-5 w-5 text-brand-blue" />
              Kiosk Ready
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Your kiosk session is ready. Click the button below to open it in a new tab.
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={openKioskTab} 
                className="flex-1 bg-brand-blue hover:bg-brand-blue/90"
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Kiosk
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(kioskLaunchUrl);
                  toast({ title: "Link copied", description: "Kiosk URL copied to clipboard" });
                }}
              >
                Copy Link
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Tip: For iPads, copy the link and paste it in Safari, then add to Home Screen.
            </p>
          </div>
        </DialogContent>
      </Dialog>
      </div>
      
      {/* QuickBooks Customer Link Dialog */}
      <QBCustomerLinkDialog
        open={showQBLinkDialog}
        onOpenChange={setShowQBLinkDialog}
        unlinkedAccounts={unlinkedAccountsWithMatches}
        onConfirm={handleQBLinkConfirm}
        isProcessing={isExporting}
      />
      
      <QBProductLinkDialog
        open={showQBProductLinkDialog}
        onOpenChange={(open) => {
          setShowQBProductLinkDialog(open);
          if (!open) {
            setPendingOrderDataForProductLink([]);
            setPendingCustomerLinkDecisions(undefined);
            setIsExporting(false);
            setSingleOrderQBSending(false);
          }
        }}
        unlinkedProducts={unlinkedProductsWithMatches}
        onConfirm={handleQBProductLinkConfirm}
        isProcessing={isExporting}
      />
      
      {/* Admin Order Creation Modal */}
      <AdminOrderModal
        open={isAdminOrderModalOpen}
        onOpenChange={setIsAdminOrderModalOpen}
      />
      
      {/* Broken Link Dialog - for customers deleted from QuickBooks */}
      <Dialog open={showBrokenLinkDialog} onOpenChange={(open) => {
        if (!open && !brokenLinkConfirming) {
          // User is closing the dialog (via X, escape, or backdrop) - not via confirm button
          setShowBrokenLinkDialog(false);
          setBrokenLinkAccounts([]);
          setIsExporting(false);
          toast({
            title: "Export cancelled",
            description: "QuickBooks invoice creation was cancelled. The broken customer links need to be resolved before sending invoices.",
          });
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              QuickBooks Customer Missing
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              The following customer(s) were linked to QuickBooks, but the linked customer no longer exists in QuickBooks. 
              They may have been deleted or merged. Please choose how to proceed for each:
            </p>
            
            {brokenLinkAccounts.map((account) => (
              <div key={account.accountId} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{account.accountName}</h4>
                    <p className="text-xs text-gray-500">
                      Was linked to QB Customer ID: {account.storedQbCustomerId}
                    </p>
                  </div>
                </div>
                
                {account.potentialMatches.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Potential matches found:</p>
                    <Select
                      onValueChange={(value) => {
                        const updatedAccounts = brokenLinkAccounts.map(a => 
                          a.accountId === account.accountId 
                            ? { ...a, selectedMatch: value }
                            : a
                        );
                        setBrokenLinkAccounts(updatedAccounts as any);
                      }}
                    >
                      <SelectTrigger className="w-full" data-testid={`select-broken-link-${account.accountId}`}>
                        <SelectValue placeholder="Select a QuickBooks customer or create new..." />
                      </SelectTrigger>
                      <SelectContent>
                        {account.potentialMatches.map((match) => (
                          <SelectItem key={match.id} value={match.id}>
                            <div className="flex items-center gap-2">
                              <span>{match.displayName}</span>
                              {match.matchType === 'exact' && (
                                <Badge variant="default" className="text-xs bg-green-100 text-green-700">
                                  Exact match
                                </Badge>
                              )}
                              {match.matchType === 'partial' && (
                                <Badge variant="secondary" className="text-xs">
                                  Similar
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                        <SelectItem value="create_new">
                          <span className="text-blue-600">Create new customer in QuickBooks</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded p-3 text-sm text-gray-600">
                    No matching customers found in QuickBooks. A new customer will be created when you send the invoice.
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowBrokenLinkDialog(false)}
              data-testid="btn-cancel-broken-link"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Build decisions from selections
                const decisions: LinkDecision[] = brokenLinkAccounts.map(account => {
                  const selectedMatch = (account as any).selectedMatch;
                  if (selectedMatch && selectedMatch !== 'create_new') {
                    return {
                      accountId: account.accountId,
                      decision: 'link' as const,
                      qbCustomerId: selectedMatch
                    };
                  }
                  return {
                    accountId: account.accountId,
                    decision: 'create_new' as const
                  };
                });
                handleBrokenLinkConfirm(decisions);
              }}
              disabled={isExporting}
              data-testid="btn-confirm-broken-link"
            >
              {isExporting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                "Continue with Invoice"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
